import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { InvoiceResponse, VisitData } from '../../pages/billing'

export interface DoctorBillingDetails {
  billNo: string
  billType: string
  visitNo: string
  regDate: string
  department: string
  doctor: string
  ratePlan: string
  referredBy: string
  openDate: string
  openTime: string
  billStatus: string
  paymentStatus: string
  billingBedType: string
  label: string
  cancelReason: string
  reopenReason: string
  remarks: string
  patientWriteoffRemarks: string
  sponsorWriteoffRemarks: string
  finalizedDate: string
  discountsAuth: string
  discountPlan: string
  priorAuthDate: string
}

interface Invoice {
  id: number
  name: string | boolean
  partner_id: [number, string] | (string | number)[]
  doctor_id: [number, string] | (string | number)[]
  move_type: string
  state: string
  payment_state: string
  ref: string | boolean
}

interface BillingState {
  items: InvoiceResponse[]
  doctors: DoctorBillingDetails[]
  billData: Invoice | null
  activeInvoice: number | null
  activeUserVisitData: VisitData | null
  refresh: boolean
  isReloadBills: boolean
  isBillUpdated: boolean
}

export const newDoctorDetails: DoctorBillingDetails = {
  billNo: '',
  billType: 'Bill Now',
  visitNo: '',
  regDate: '',
  department: '',
  doctor: '',
  ratePlan: '',
  referredBy: '',
  openDate: '',
  openTime: '',
  billStatus: 'Open',
  paymentStatus: 'Unpaid',
  billingBedType: '',
  label: '',
  cancelReason: '',
  reopenReason: '',
  remarks: '',
  patientWriteoffRemarks: '',
  sponsorWriteoffRemarks: '',
  finalizedDate: '',
  discountsAuth: '',
  discountPlan: '',
  priorAuthDate: '',
}

const initialState: BillingState = {
  items: [],
  doctors: [],
  billData: null,
  activeInvoice: null,
  activeUserVisitData: null,
  refresh: false,
  isReloadBills: false,
  isBillUpdated: false,
}

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<InvoiceResponse>) => {
      state.items.push(action.payload)
    },
    clearItems: (state) => {
      state.items = []
      state.activeInvoice = null
      state.billData = null
      state.doctors = []
      state.activeUserVisitData = null
      state.refresh = false
      state.isReloadBills = false
      state.isBillUpdated = false
    },
    setSelectedBillData: (state, action) => {
      state.billData = action.payload
    },
    setSelectedActiveUserVisitData: (state, action) => {
      state.activeUserVisitData = action.payload
    },
    clearDoctorDetails: (state) => {
      state.doctors = []
    },
    setActiveInvoice: (state, action) => {
      state.activeInvoice = action.payload
    },
    setIsRefresh: (state, action) => {
      state.refresh = action.payload
    },
    setIsBillUpdated: (state, action) => {
      state.isBillUpdated = action.payload
    },
    setIsReloadBills: (state, action) => {
      state.isReloadBills = action.payload
    },
  },
})

export const selectBillingItems = (state: { billing: BillingState }) =>
  state.billing.items

export const selectDoctorDetails = (state: { billing: BillingState }) =>
  state.billing.doctors

export const selectedBillDetails = (state: { billing: BillingState }) =>
  state.billing.billData

export const getActiveInvoice = (state: { billing: BillingState }) =>
  state.billing.activeInvoice

export const getActiveUserVisitDetails = (state: { billing: BillingState }) =>
  state.billing.activeUserVisitData

export const getIsRefresh = (state: { billing: BillingState }) =>
  state.billing.refresh

export const getIsReloadBills = (state: { billing: BillingState }) =>
  state.billing.isReloadBills

export const getIsBillUpdated = (state: { billing: BillingState }) =>
  state.billing.isBillUpdated

export const {
  addItem,
  clearItems,
  clearDoctorDetails,
  setSelectedBillData,
  setActiveInvoice,
  setIsBillUpdated,
  setSelectedActiveUserVisitData,
  setIsRefresh,
  setIsReloadBills,
} = billingSlice.actions

export default billingSlice.reducer
